import React, { useState, useEffect, useRef } from 'react';
import './AIRecommendation.css';
import apiConfig from '../config/api';
import AnalyticsService from '../services/AnalyticsService';
import { AI_TIPS_SECTION_TITLE, AI_TIP_ILA, AI_TIP_TABU } from '../data/aiOutputTips';
import { isRegisteredAsIlaLease } from '../utils/ilaLeaseOwnership';
import { buildUrbanixReportHtml } from '../utils/buildUrbanixReportHtml';

const PREFETCH_FAKE_LOAD_MS = 1000;

const AIRecommendation = ({
  sectionId,
  recommendation,
  loading,
  error,
  onStartAiPrefetch,
  onGenerateRecommendation,
  feasibilityData,
  searchResults,
  onNavigateProjectPromotion,
  analysisUnlocked,
  setAnalysisUnlocked,
  disabledReason = '',
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [formErrors, setFormErrors] = useState({});

  const [codeSent, setCodeSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationToken, setVerificationToken] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [codeResent, setCodeResent] = useState(false);
  const [awaitingReportAfterVerify, setAwaitingReportAfterVerify] = useState(false);
  const lastContactRef = useRef(null);
  /** idle → brief_load (fake) → contact (lead form before recommendation exists) */
  const [prefetchUiPhase, setPrefetchUiPhase] = useState('idle');
  const prefetchFakeLoadTimerRef = useRef(null);

  useEffect(() => {
    if (recommendation) setAwaitingReportAfterVerify(false);
  }, [recommendation]);

  useEffect(() => {
    if (error) {
      setAwaitingReportAfterVerify(false);
      setPrefetchUiPhase('idle');
    }
  }, [error]);

  useEffect(
    () => () => {
      if (prefetchFakeLoadTimerRef.current) {
        clearTimeout(prefetchFakeLoadTimerRef.current);
        prefetchFakeLoadTimerRef.current = null;
      }
    },
    []
  );

  // AI Feedback state
  const [feedbackVote, setFeedbackVote] = useState(null); // 'up' | 'down' | null
  const [feedbackFormOpen, setFeedbackFormOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackTags, setFeedbackTags] = useState([]);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  const FEEDBACK_TAGS_UP = ['מדויק', 'מפורט', 'שימושי', 'ברור'];
  const FEEDBACK_TAGS_DOWN = ['לא מדויק', 'חסר מידע', 'לא רלוונטי', 'לא ברור'];

  const handleFeedbackVote = async (vote) => {
    setFeedbackVote(vote);
    setFeedbackTags([]);
    setFeedbackText('');
    setFeedbackSubmitted(false);

    if (vote === 'up') {
      setFeedbackFormOpen(false);
      setFeedbackSubmitting(true);
      try {
        await fetch(`${apiConfig.apiBaseUrl}/api/contact`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ source: 'ai-feedback', data: {
            vote: 'up', tags: '', comment: '',
            address: searchResults?.address || '',
            gush: searchResults?.gush || '', helka: searchResults?.helka || '',
            likelihood: recommendation?.likelihood || '',
            timestamp: new Date().toISOString(),
          }}),
        });
      } catch (err) { console.error('Feedback submit error:', err); }
      setFeedbackSubmitting(false);
      setFeedbackSubmitted(true);
    } else {
      setFeedbackFormOpen(true);
    }
  };

  const toggleFeedbackTag = (tag) => {
    setFeedbackTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const submitFeedback = async () => {
    setFeedbackSubmitting(true);
    try {
      const feedbackData = {
        vote: feedbackVote,
        tags: feedbackTags.join(', '),
        comment: feedbackText,
        address: searchResults?.address || '',
        gush: searchResults?.gush || '',
        helka: searchResults?.helka || '',
        likelihood: recommendation?.likelihood || '',
        timestamp: new Date().toISOString(),
      };
      await fetch(`${apiConfig.apiBaseUrl}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: 'ai-feedback', data: feedbackData }),
      });
      setFeedbackSubmitted(true);
      setFeedbackFormOpen(false);
    } catch (err) {
      console.error('Feedback submit error:', err);
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const validateNameEmail = () => {
    const errors = {};
    if (!name.trim()) errors.name = 'נא למלא שם מלא';
    if (!email.trim()) errors.email = 'נא למלא אימייל';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = 'כתובת אימייל לא תקינה';
    return errors;
  };

  const handleSendVerificationCode = async () => {
    const errors = validateNameEmail();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    setSendingCode(true);
    try {
      const response = await fetch(`${apiConfig.apiBaseUrl}/api/contact?action=send-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await response.json();
      if (data.bypassed) {
        const contact = { name: name.trim(), email: email.trim(), aiAccessToken: data.aiAccessToken || '' };
        lastContactRef.current = contact;
        const needsFullGeneration = !recommendation;
        if (needsFullGeneration) setAwaitingReportAfterVerify(true);
        try {
          if (onGenerateRecommendation) await onGenerateRecommendation(contact);
          if (setAnalysisUnlocked) setAnalysisUnlocked(true);
        } finally {
          if (needsFullGeneration) setAwaitingReportAfterVerify(false);
        }
        return;
      }
      if (data.success && data.token) {
        setVerificationToken(data.token);
        setCodeSent(true);
        setVerificationCode('');
      } else {
        setFormErrors({ email: data.error || 'שגיאה בשליחת קוד האימות' });
      }
    } catch (err) {
      setFormErrors({ email: 'שגיאה בשליחת קוד האימות. נסו שוב.' });
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyAndCreateReport = async () => {
    if (!verificationCode.trim()) {
      setFormErrors({ code: 'נא להזין את הקוד' });
      return;
    }
    setVerifying(true);
    setFormErrors({});
    try {
      const response = await fetch(`${apiConfig.apiBaseUrl}/api/contact?action=verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: verificationToken, code: verificationCode.trim() }),
      });
      const data = await response.json();
      if (data.verified) {
        const contact = { name: name.trim(), email: email.trim(), aiAccessToken: data.aiAccessToken || '' };
        lastContactRef.current = contact;
        const showPostVerifyLoading = !recommendation;
        if (showPostVerifyLoading) setAwaitingReportAfterVerify(true);
        try {
          if (onGenerateRecommendation) await onGenerateRecommendation(contact);
          if (setAnalysisUnlocked) setAnalysisUnlocked(true);
        } finally {
          if (showPostVerifyLoading) setAwaitingReportAfterVerify(false);
        }
      } else {
        setFormErrors({ code: data.error || 'הקוד שגוי' });
      }
    } catch (err) {
      setFormErrors({ code: 'שגיאה באימות. נסו שוב.' });
    } finally {
      setVerifying(false);
    }
  };

  const handleResendCode = async () => {
    setSendingCode(true);
    setCodeResent(false);
    try {
      const response = await fetch(`${apiConfig.apiBaseUrl}/api/contact?action=send-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await response.json();
      if (data.success && data.token) {
        setVerificationToken(data.token);
        setVerificationCode('');
        setCodeSent(true);
        setCodeResent(true);
        setFormErrors({});
        setTimeout(() => setCodeResent(false), 3000);
      }
    } catch (err) {
      // silently fail
    } finally {
      setSendingCode(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'name') setName(value);
    if (field === 'email') setEmail(value);
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleStartPrefetchFlow = () => {
    if (prefetchFakeLoadTimerRef.current) {
      clearTimeout(prefetchFakeLoadTimerRef.current);
      prefetchFakeLoadTimerRef.current = null;
    }
    if (onStartAiPrefetch) {
      onStartAiPrefetch();
      setPrefetchUiPhase('brief_load');
      prefetchFakeLoadTimerRef.current = setTimeout(() => {
        prefetchFakeLoadTimerRef.current = null;
        setPrefetchUiPhase('contact');
      }, PREFETCH_FAKE_LOAD_MS);
    } else if (onGenerateRecommendation) {
      onGenerateRecommendation(null);
    }
  };

  const renderAiLeadCaptureForm = ({ gateTitle, microcopy, verifyButtonLabel = 'אמתו והמשיכו לצפייה המלאה' }) => (
    <div className="ai-unlock-gate">
      <p className="ai-unlock-gate-title">{gateTitle}</p>
      <div className="ai-contact-form ai-post-report-form">
        <div className="form-inputs ai-unlock-row">
          <div className="input-wrapper">
            <input
              id="ai-lead-name"
              name="full_name"
              type="text"
              autoComplete="name"
              placeholder="שם מלא"
              value={name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={`ai-form-input ${formErrors.name ? 'input-error' : ''}`}
              disabled={sendingCode || verifying}
            />
            {formErrors.name && <span className="error-message">{formErrors.name}</span>}
          </div>
          <div className="input-wrapper">
            <input
              id="ai-lead-email"
              name="email"
              type="email"
              autoComplete="email"
              inputMode="email"
              placeholder="כתובת מייל"
              value={email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={`ai-form-input ai-form-input-email ${formErrors.email ? 'input-error' : ''}`}
              disabled={sendingCode || verifying}
            />
            {formErrors.email && <span className="error-message">{formErrors.email}</span>}
          </div>
        </div>
        {!codeSent ? (
          <button
            type="button"
            className="btn btn-primary ai-form-submit ai-unlock-primary-btn"
            onClick={handleSendVerificationCode}
            disabled={sendingCode || verifying}
          >
            {sendingCode ? 'שולח...' : 'שלחו קוד אימות למייל'}
          </button>
        ) : (
          <>
            <div className="input-wrapper ai-code-field-wrap ai-code-field-full">
              <input
                id="ai-verification-code"
                name="verification_code"
                type="text"
                autoComplete="one-time-code"
                inputMode="numeric"
                placeholder="קוד אימות"
                value={verificationCode}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setVerificationCode(val);
                  if (formErrors.code) setFormErrors((prev) => ({ ...prev, code: '' }));
                }}
                className={`ai-form-input ${formErrors.code ? 'input-error' : ''}`}
                disabled={verifying}
              />
              {formErrors.code && <span className="error-message">{formErrors.code}</span>}
            </div>
            <button
              type="button"
              className="btn btn-primary ai-form-submit ai-unlock-primary-btn"
              onClick={handleVerifyAndCreateReport}
              disabled={verifying}
            >
              {verifying ? 'מאמת...' : verifyButtonLabel}
            </button>
          </>
        )}
        {codeSent && (
          <div className="ai-post-report-resend">
            <button type="button" className="ai-text-link" onClick={handleResendCode} disabled={sendingCode}>
              {codeResent ? 'נשלח מחדש' : sendingCode ? 'שולח...' : 'שלחו קוד מחדש'}
            </button>
            <button
              type="button"
              className="ai-text-link muted"
              onClick={() => {
                setCodeSent(false);
                setVerificationCode('');
                setVerificationToken('');
                setFormErrors({});
              }}
            >
              שנו אימייל
            </button>
          </div>
        )}
        <p className="ai-post-report-microcopy">{microcopy}</p>
      </div>
    </div>
  );

  const handlePrint = () => {
    AnalyticsService.trackPDFDownload(searchResults?.address);
    const html = buildUrbanixReportHtml(recommendation, feasibilityData, searchResults);
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) return;
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 500);
  };

  const getLikelihoodConfig = (likelihood) => {
    const configs = {
      very_low: { label: 'נמוך מאוד', scaleIndex: 0 },
      low: { label: 'נמוך', scaleIndex: 1 },
      medium: { label: 'בינוני', scaleIndex: 2 },
      high: { label: 'גבוה', scaleIndex: 3 },
      very_high: { label: 'גבוה מאוד', scaleIndex: 4 }
    };
    return configs[likelihood] || { label: 'בינוני', scaleIndex: 2 };
  };

  const getEstimateRangeLabel = (range) => {
    const ranges = {
      very_low: 'מבנה בעל מספר דירות נמוך מאוד',
      low: 'מבנה בעל מספר דירות נמוך',
      medium: 'מבנה בעל מספר דירות בינוני',
      high: 'מבנה בעל מספר דירות גבוה',
      very_high: 'מבנה בעל מספר דירות גבוה מאוד'
    };
    return ranges[range] || 'לא צוין';
  };

  const getDerivedEstimateRange = (estimatedApartments) => {
    if (!Number.isFinite(estimatedApartments)) return null;
    if (estimatedApartments >= 50) return 'very_high';
    if (estimatedApartments >= 20) return 'high';
    if (estimatedApartments >= 10) return 'medium';
    if (estimatedApartments >= 5) return 'low';
    if (estimatedApartments >= 1) return 'very_low';
    return null;
  };

  // Format text: detect numbered items like "1) ... 2) ..." and render as styled list
  const formatNumberedText = (text) => {
    if (!text) return '';
    // Split on numbered pattern like "1) ", "2) " at line boundaries only (avoids matching years like "1980)")
    const parts = text.split(/(?:^|\n)\s*(\d{1,2})\)\s*/);
    // parts will be: [prefix, "1", text1, "2", text2, ...]
    // If no numbered items found, return plain text
    if (parts.length <= 1) return text;

    const items = [];
    let i = 0;
    // First part before any number (usually empty or whitespace)
    if (parts[0] && parts[0].trim()) {
      items.push({ type: 'text', content: parts[0].trim() });
    }
    i = 1;
    while (i < parts.length - 1) {
      const num = parts[i];
      const content = (parts[i + 1] || '').trim();
      if (content) {
        items.push({ type: 'item', num, content });
      }
      i += 2;
    }

    // If we only found one item, just return plain text
    if (items.filter(it => it.type === 'item').length <= 1) {
      return text.replace(/\s+(\d+)\)/g, (m, n) => Number(n) > 1 ? `\n${n})` : m);
    }

    return (
      <>
        {items.map((item, idx) =>
          item.type === 'text' ? (
            <span key={idx}>{item.content} </span>
          ) : (
            <div key={idx} className="ai-list-item">
              <span className="ai-list-num">{item.num}</span>
              <span>{item.content}</span>
            </div>
          )
        )}
      </>
    );
  };
  
  if (loading && awaitingReportAfterVerify) {
    return (
      <div id={sectionId} className="ai-recommendation loading ai-post-verify-loading">
        <div className="ai-header ai-header-initial ai-post-verify-header">
          <h3>המייל אומת בהצלחה</h3>
        </div>
        <div className="ai-loading-content">
          <div className="spinner" />
          <p>הסוכן של אורבניקס כבר מנתח את הנתונים ומכין עבורכם חוות דעת מקצועית.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div id={sectionId} className="ai-recommendation loading">
        <div className="ai-header ai-header-initial">
          <div className="ai-icon"><i className="fi fi-rr-robot"></i></div>
          <h3>מנתח את הנכס שלכם...</h3>
        </div>
        <div className="ai-loading-content">
          <div className="spinner"></div>
          <p>בודק את כל הנתונים שאספנו ומכין לכם המלצות מותאמות אישית</p>
          <p className="ai-loading-subtext">(עוד כמה שניות...)</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div id={sectionId} className="ai-recommendation error">
        <div className="ai-header ai-header-initial">
          <div className="ai-icon"><i className="fi fi-rr-exclamation"></i></div>
          <h3>לא הצלחנו ליצור את הניתוח</h3>
        </div>
        <p className="error-message">אירעה שגיאה בעת יצירת הניתוח. אפשר לנסות שוב. לרוב זה נפתר תוך שניות.</p>
        {onGenerateRecommendation && (
          <button
            type="button"
            className="btn btn-outline btn-sm"
            style={{ marginTop: '12px' }}
            onClick={() => onGenerateRecommendation(null)}
          >
            נסו שוב
          </button>
        )}
      </div>
    );
  }

  if (!recommendation) {
    if (onGenerateRecommendation || onStartAiPrefetch) {
      return (
        <div id={sectionId} className={`ai-recommendation initial ai-post-report-block ${disabledReason ? 'ai-recommendation-disabled' : ''}`}>
          <div className="ai-header ai-header-initial ai-post-report-header">
            <h3>עכשיו מגיע החלק המעניין</h3>
          </div>
          <div className="ai-initial-content ai-post-report-content">
            <div className="ai-agent-dock">
              <div className="ai-agent-visual" aria-hidden="true">
                <span className="ai-agent-ring ai-agent-ring--outer" />
                <span className="ai-agent-ring ai-agent-ring--rotate" />
                <div className="ai-agent-core">
                  <img
                    src="/urbanix-robot.png"
                    alt=""
                    className="ai-agent-app-icon"
                    width={80}
                    height={80}
                    decoding="async"
                  />
                </div>
              </div>
              <div className="ai-agent-caption">
                <span className="ai-agent-caption-name">סוכן אורבניקס</span>
                <span className="ai-agent-caption-role">יועץ התחדשות עירונית · מבוסס בינה מלאכותית</span>
              </div>
            </div>
            <p className="ai-description">
              סוכן אורבניקס מסכם את כל הממצאים לחוות דעת אחת.
              <br />
              תראו במהירות עד כמה ריאלי לקדם התחדשות עירונית בנכס שלכם.
            </p>
            {disabledReason && (
              <div className="ai-disabled-notice" role="status">
                <i className="fi fi-rr-triangle-warning"></i>
                <p>{disabledReason}</p>
              </div>
            )}
            {prefetchUiPhase === 'idle' && (
              <button
                type="button"
                className="btn btn-primary btn-lg ai-cta-generate-single"
                onClick={handleStartPrefetchFlow}
                disabled={Boolean(disabledReason)}
              >
                {disabledReason ? 'דוח AI לא זמין כרגע' : 'קבלו ניתוח חכם בקליק'}
              </button>
            )}
            {prefetchUiPhase === 'brief_load' && (
              <div className="ai-prefetch-fake-loader" aria-live="polite">
                <div className="ai-prefetch-fake-loader__spinner" />
                <p className="ai-prefetch-fake-loader__text">מתחילים לנסח את הניתוח…</p>
              </div>
            )}
            {prefetchUiPhase === 'contact' &&
              renderAiLeadCaptureForm({
                gateTitle: 'דוח ה־AI יישלח אליכם למייל כקובץ PDF',
                microcopy:
                  'הזינו שם ואת כתובת המייל שאליה תרצו לקבל את הקובץ. נשלח לכם קוד אימות קצר, ובסיום האימות דוח ה־AI יישלח לאותה כתובת מייל כקובץ PDF. בינתיים הניתוח כבר מתקדם ברקע.',
              })}
          </div>
        </div>
      );
    }
    return null;
  }

  const teaserText = (() => {
    if (recommendation.openingStatement?.trim()) return recommendation.openingStatement.trim();
    if (recommendation.summary?.trim()) {
      const first = recommendation.summary.split(/\n\s*\n/)[0]?.trim();
      if (first) return first.length > 420 ? `${first.slice(0, 420)}…` : first;
    }
    return 'הניתוח מוכן. כדי לקרוא את כל ההמלצות והפירוט, יש להשלים אימות מייל למטה.';
  })();

  if (!analysisUnlocked) {
    return (
      <div id={sectionId} className="ai-recommendation ai-preview-locked ai-post-report-block">
        <div className="ai-header ai-header-initial ai-post-report-header">
          <div className="ai-header-text">
            <h3>תצוגה מקדימה: ניתוח הסוכן החכם</h3>
            <span className="ai-header-sub">זוהי נקודת הפתיחה של הדוח. הניתוח המלא מחכה מאחורי אימות מייל מהיר</span>
          </div>
        </div>
        <div className="ai-opening ai-preview-teaser">
          <p className="ai-opening-text">{teaserText}</p>
        </div>
        {renderAiLeadCaptureForm({
          gateTitle: 'לצפייה בניתוח המלא ולשליחת הדוח למייל כ־PDF',
          microcopy:
            'נשלח אליכם קוד קצר לאימות. אחרי האימות ייפתח הניתוח המלא, ודוח ה־AI יישלח אליכם למייל כקובץ PDF לכתובת שציינתם.',
        })}
      </div>
    );
  }

  const likelihoodConfig = getLikelihoodConfig(recommendation.likelihood);
  const markerLeft = (1 - ((likelihoodConfig.scaleIndex + 0.5) / 5)) * 100;

  // Derive apartment estimate data
  const estimatedApartments = recommendation.buildingAnalysis?.estimatedApartments;
  const derivedRange = getDerivedEstimateRange(estimatedApartments);
  const rangeToShow = derivedRange || recommendation.buildingAnalysis?.estimateRange;

  // Parse summary into labeled blocks, extracting inline headings as labels
  const summaryBlocks = (() => {
    if (!recommendation.summary) return [];
    
    // First, split on known section headings that might be merged into one paragraph
    // This handles cases where the AI doesn't separate them with \n\n
    let text = recommendation.summary;
    const sectionHeadings = ['חוזקות:', 'חולשות:', 'צעדים להמשך:'];
    for (const heading of sectionHeadings) {
      // Insert \n\n before each heading if it's not already at the start of a paragraph
      text = text.replace(new RegExp(`(?<!^)(?<!\n\n)\\s*${heading}`, 'g'), `\n\n${heading}`);
    }
    
    const paragraphs = text
      .split(/\n\s*\n/)
      .map((part) => part.trim())
      .filter(Boolean);
    const fallbackLabels = ['סקירת נתונים', 'חוזקות', 'חולשות', 'צעדים להמשך'];
    // Pattern: line starts with a heading like "חוזקות:" or "צעדים להמשך:"
    const headingPattern = /^([^\n:]{2,30}):\s*/;
    return paragraphs.map((text, i) => {
      const match = text.match(headingPattern);
      if (match) {
        return { label: match[1].trim(), text: text.slice(match[0].length).trim() };
      }
      return { label: i < fallbackLabels.length ? fallbackLabels[i] : null, text };
    });
  })();

  return (
    <div id={sectionId} className="ai-recommendation">
      {/* Header */}
      <div className="ai-header">
        <div className="ai-header-text">
          <h3>ניתוח הסוכן החכם של אורבניקס</h3>
          <span className="ai-header-sub">ניתוח מבוסס בינה מלאכותית על בסיס הנתונים שנאספו</span>
        </div>
      </div>

      {/* Opening Statement */}
      {recommendation.openingStatement && (
        <div className="ai-opening">
          <i className="fi fi-rr-document-signed ai-opening-icon"></i>
          <p className="ai-opening-text">{recommendation.openingStatement}</p>
        </div>
      )}

      {/* Analysis Sections — single-column flow */}
      <div className="ai-sections">

        {/* Section 1: Basic Data */}
        <div className="ai-section-card">
          <div className="ai-section-header">
            <div className="ai-section-icon"><i className="fi fi-rr-marker"></i></div>
            <h4>נתונים בסיסיים</h4>
          </div>
          <div className="ai-section-body">
            <div className="ai-analysis-item">
              <span className="ai-item-label">מיקום</span>
              <p>{recommendation.locationAnalysis}</p>
            </div>
            <div className="ai-analysis-item">
              <span className="ai-item-label">ייעוד קרקע</span>
              <p>{recommendation.landUseAnalysis}</p>
            </div>
            <div className="ai-analysis-item">
              <span className="ai-item-label">גיל מבנה</span>
              <p>{recommendation.yearBuiltAnalysis}</p>
            </div>
          </div>
        </div>

        {/* Section 2: Building Characteristics */}
        <div className="ai-section-card">
          <div className="ai-section-header">
            <div className="ai-section-icon"><i className="fi fi-rr-building"></i></div>
            <h4>מאפייני מבנה</h4>
          </div>
          {/* Apartment estimate — inline within the section */}
          <div className="ai-metric-inline">
            <span className="ai-metric-inline-label">הערכת דירות:</span>
            <span className="ai-metric-inline-value">
              {Number.isFinite(estimatedApartments) ? estimatedApartments : '—'}
            </span>
            {rangeToShow && (
              <span className="ai-metric-inline-range">
                {' · '}{getEstimateRangeLabel(rangeToShow)}
              </span>
            )}
          </div>
          <div className="ai-section-body">
            <div className="ai-analysis-item">
              <span className="ai-item-label">ניתוח מבנה</span>
              <p>{recommendation.buildingAnalysis?.analysis}</p>
            </div>
            <div className="ai-analysis-item">
              <span className="ai-item-label">קומת עמודים</span>
              <p>{recommendation.elevatedGroundFloorAnalysis}</p>
            </div>
          </div>
        </div>

        {/* Section 3: Planning & Environment */}
        <div className="ai-section-card">
          <div className="ai-section-header">
            <div className="ai-section-icon"><i className="fi fi-rr-city"></i></div>
            <h4>תכנון, מדיניות וסביבה</h4>
          </div>
          <div className="ai-section-body">
            <div className="ai-analysis-item">
              <span className="ai-item-label">שימור</span>
              <p>{recommendation.conservationAnalysis}</p>
            </div>
            <div className="ai-analysis-item">
              <span className="ai-item-label">סוציו-אקונומי</span>
              <p>{recommendation.socioEconomicAnalysis}</p>
            </div>
            <div className="ai-analysis-item">
              <span className="ai-item-label">תכניות</span>
              <p>{recommendation.plansAnalysis}</p>
            </div>
            <div className="ai-analysis-item">
              <span className="ai-item-label">תכנית 77/78</span>
              <p>{recommendation.section7778Analysis}</p>
            </div>
          </div>
        </div>

        {/* Section 4: Summary & Recommendations — visually distinct conclusion */}
        {summaryBlocks.length > 0 && (
          <div className="ai-section-card ai-section-summary">
            <div className="ai-section-header">
              <div className="ai-section-icon"><i className="fi fi-rr-clipboard-list-check"></i></div>
              <h4>סיכום והמלצות</h4>
            </div>
            <div className="ai-section-body">
              {summaryBlocks.map((block, index) => (
                <div key={index} className={`ai-analysis-item ai-summary-block-${index}`}>
                  {block.label && <span className="ai-item-label">{block.label}</span>}
                  <div className="ai-summary-block-text">{formatNumberedText(block.text)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tips — fixed product section (after summary, before likelihood) */}
        <div className="ai-section-card ai-section-tips">
          <div className="ai-section-header">
            <div className="ai-section-icon"><i className="fi fi-rr-lightbulb"></i></div>
            <h4>{AI_TIPS_SECTION_TITLE}</h4>
          </div>
          <div className="ai-section-body ai-tips-body">
            {isRegisteredAsIlaLease(searchResults) && (
              <div className="ai-tip-block">
                <span className="ai-tip-title">{AI_TIP_ILA.title}</span>
                <p className="ai-tip-text">{AI_TIP_ILA.body}</p>
                <p className="ai-tip-disclaimer">{AI_TIP_ILA.disclaimer}</p>
              </div>
            )}
            <div className="ai-tip-block">
              <span className="ai-tip-title">{AI_TIP_TABU.title}</span>
              <p className="ai-tip-text">{AI_TIP_TABU.body}</p>
              <p className="ai-tip-text ai-tip-follow">{AI_TIP_TABU.followUp}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Likelihood Scale */}
      <div className="eligibility-scale ai-likelihood-scale">
        <div className="eligibility-scale-header">
          <span>הסיכוי שפרויקט יקרה אצלכם</span>
        </div>
        <div className="eligibility-scale-visual" data-direction="rtl">
          <div className="eligibility-scale-bar">
            <div
              className="eligibility-scale-marker"
              style={{ left: `${markerLeft}%` }}
            >
              <span className="eligibility-scale-marker-value" aria-hidden="true"></span>
            </div>
          </div>
          <div className="eligibility-scale-ticks">
            {[0, 1, 2, 3, 4].map((value) => (
              <div key={value} className="eligibility-scale-tick">
                <span aria-hidden="true"></span>
              </div>
            ))}
          </div>
          <div className="eligibility-scale-legend">
            <span>גבוה מאוד</span>
            <span>גבוה</span>
            <span>בינוני</span>
            <span>נמוך</span>
            <span>נמוך מאוד</span>
          </div>
        </div>
      </div>

      {onNavigateProjectPromotion && (
        <div className="ai-promotion-cta">
          <button type="button" className="ai-promotion-cta-button" onClick={onNavigateProjectPromotion}>
            <span className="ai-promotion-cta-label">רוצים לבדוק גם קידום פרויקט?</span>
            <i className="fi fi-rr-angle-small-left ai-promotion-cta-icon" aria-hidden="true" />
          </button>
        </div>
      )}

      {/* AI Feedback */}
      <div className="ai-feedback">
        <div className="ai-feedback-prompt">
          <span>האם הניתוח היה שימושי?</span>
          <div className="ai-feedback-thumbs">
            <button
              type="button"
              className={`ai-feedback-thumb ${feedbackVote === 'up' ? 'selected' : ''}`}
              onClick={() => handleFeedbackVote('up')}
              disabled={feedbackSubmitted}
              title="שימושי"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 10v12"/><path d="M15 5.88L14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z"/></svg>
            </button>
            <button
              type="button"
              className={`ai-feedback-thumb ${feedbackVote === 'down' ? 'selected' : ''}`}
              onClick={() => handleFeedbackVote('down')}
              disabled={feedbackSubmitted}
              title="לא שימושי"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 14V2"/><path d="M9 18.12L10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z"/></svg>
            </button>
          </div>
        </div>

        {feedbackFormOpen && !feedbackSubmitted && (
          <div className="ai-feedback-form">
            <div className="ai-feedback-tags">
              {(feedbackVote === 'up' ? FEEDBACK_TAGS_UP : FEEDBACK_TAGS_DOWN).map(tag => (
                <button
                  key={tag}
                  type="button"
                  className={`ai-feedback-tag ${feedbackTags.includes(tag) ? 'selected' : ''}`}
                  onClick={() => toggleFeedbackTag(tag)}
                >
                  {tag}
                </button>
              ))}
            </div>
            <textarea
              id="ai-feedback-text"
              name="feedback_text"
              autoComplete="off"
              className="ai-feedback-textarea"
              placeholder="ספרו לנו עוד (אופציונלי)..."
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              rows={3}
            />
            <div className="ai-feedback-actions">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={submitFeedback}
                disabled={feedbackSubmitting}
              >
                {feedbackSubmitting ? 'שולח...' : 'שלח משוב'}
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setFeedbackFormOpen(false)}
              >
                ביטול
              </button>
            </div>
          </div>
        )}

        {feedbackSubmitted && (
          <div className="ai-feedback-thanks">
            <i className="fi fi-rr-check-circle"></i>
            <span>תודה על המשוב!</span>
          </div>
        )}
      </div>

      {/* Download PDF */}
      <div className="ai-footer-actions">
        <button type="button" className="btn btn-outline btn-lg" onClick={handlePrint}>
          <i className="fi fi-rr-download"></i>
          הורדת PDF
        </button>
      </div>
    </div>
  );
};

export default AIRecommendation;
